import { 
  collection,
  doc,
  addDoc,
  setDoc, 
  getDoc, 
  getDocs,
  query,
  where,
  serverTimestamp,
  Timestamp,
  DocumentReference,
  Firestore,
  updateDoc,
  or,
  orderBy,
  limit,
  startAfter,
  enableNetwork,
  disableNetwork,
  onSnapshot,
  getDocsFromCache,
  getDocsFromServer,
      QueryDocumentSnapshot,
    FieldValue,
    Unsubscribe
} from 'firebase/firestore';
import { db, getUserId } from './firebase';
import { Customer, Repair, RepairStatus, PaymentStatus, CustomerWithId, RepairWithId } from '../types';
import { normalizePhoneNumber } from '../utils/phoneUtils';
import { withAuthentication } from './authService';
import { toDateSafe, toTimestampSafe } from '../utils/dateUtils';

// Track active subscriptions to avoid duplicate listeners
const activeSubscriptions = new Map<string, Unsubscribe>();

// Function to check if network is online
const isOnline = (): boolean => {
  return navigator.onLine;
};

// Toggle network for testing offline mode
const _toggleNetwork = async (online: boolean): Promise<void> => {
  if (!db) {
    throw new Error('Firestore is not initialized');
  }

  try {
    if (online) {
      await enableNetwork(db);
      console.log('Network connection enabled');
    } else {
      await disableNetwork(db);
      console.log('Network connection disabled');
    }
  } catch (error) {
    console.error('Error toggling network:', error);
    throw new Error('Network bağlantısı değiştirilirken bir hata oluştu');
  }
};

// Fix for the serverTimestamp issue - add type annotation for proper handling in creates
// We need to handle where Timestamp is expected but serverTimestamp() returns FieldValue
type TimestampOrField = Timestamp | FieldValue;

/**
 * Batch load customers from references and update cache
 * @param customerRefs Array of customer references
 * @returns Map of customer data
 */
const batchLoadCustomers = async (customerRefs: DocumentReference[]): Promise<Map<string, Customer>> => {
  const result = new Map<string, Customer>();
  if (!db) {
    console.error('Firestore is not initialized in batchLoadCustomers');
    return result;
  }

  // Fetch all customer documents. Firestore's SDK will handle caching.
  try {
    const snapshots = await Promise.all(
      customerRefs.map(async (ref) => {
        try {
          // getDoc will use Firestore's cache if available and appropriate
          return await getDoc(ref);
        } catch (error) {
          console.warn(`Error loading customer ${ref.id}:`, error);
          return null;
        }
      })
    );

    snapshots.forEach((snapshot) => {
      if (snapshot && snapshot.exists()) {
        const refKey = snapshot.ref.id || snapshot.ref.path || '';
        if (!refKey) return;

        const customerData = snapshot.data() as Customer;
        result.set(refKey, customerData);
      }
    });

    return result;
  } catch (error) {
    console.error('Error batch loading customers:', error);
    return result; // Return whatever could be loaded
  }
};

/**
 * Find a customer by phone number
 */
const _findCustomerByPhone = async (phone: string): Promise<{ id: string; customer: Customer } | null> => {
  if (!db) {
    throw new Error('Firestore is not initialized');
  }

  try {
    const normalizedPhone = normalizePhoneNumber(phone);
    const userId = getUserId();
    const customersRef = collection(db, 'customers');
    const q = query(
      customersRef,
      where('userId', '==', userId),
      where('phone', '==', normalizedPhone)
    );

    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }

    const docData = {
      id: querySnapshot.docs[0].id,
      customer: querySnapshot.docs[0].data() as Customer
    };
    
    return docData;
  } catch (error) {
    console.error('Error finding customer:', error);
    throw new Error('Müşteri arama sırasında bir hata oluştu');
  }
};

/**
 * Create a new customer
 */
const _createCustomer = async (customer: Omit<Customer, 'createdAt'>): Promise<string> => {
  if (!db) {
    throw new Error('Firestore is not initialized');
  }

  try {
    const userId = getUserId();
    
    // Normalize the phone number before storing
    const normalizedCustomer = {
      ...customer,
      phone: normalizePhoneNumber(customer.phone)
    };
    
    const customerData: any = { // Use any to work around typing issue
      ...normalizedCustomer,
      userId, // Add userId for data segmentation
      createdAt: serverTimestamp()
    };
    
    const docRef = await addDoc(collection(db, 'customers'), customerData);
    return docRef.id;
  } catch (error) {
    console.error('Error creating customer:', error);
    throw new Error('Müşteri oluşturulurken bir hata oluştu');
  }
};

/**
 * Create a new repair record
 */
const _createRepair = async (
  repair: {
    itemType: string;
    description: string;
    priceEstimate?: number;
    paymentStatus?: PaymentStatus;
    paidAmount?: number;
  },
  customerId: string
): Promise<string> => {
  if (!db) {
    throw new Error('Firestore is not initialized');
  }

  try {
    const userId = getUserId();
    const customerRef = doc(db, 'customers', customerId);
    
    // Calculate payment status and remaining balance
    const paidAmount = repair.paidAmount || 0;
    const priceEstimate = repair.priceEstimate || 0;
    
    let paymentStatus = repair.paymentStatus || PaymentStatus.Unpaid;
    if (paidAmount > 0 && priceEstimate > 0) {
      if (paidAmount >= priceEstimate) {
        paymentStatus = PaymentStatus.FullyPaid;
      } else {
        paymentStatus = PaymentStatus.PartiallyPaid;
      }
    }
    
    const remainingBalance = Math.max(0, priceEstimate - paidAmount);
    
    const repairData: any = { // Use any to work around typing issue
      itemType: repair.itemType,
      description: repair.description,
      priceEstimate: repair.priceEstimate,
      status: RepairStatus.Pending,
      paymentStatus,
      paidAmount,
      remainingBalance,
      pickedUp: false,
      userId, // Add userId for data segmentation
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    const repairWithCustomer = {
      ...repairData,
      customerId: customerRef
    };
    
    const docRef = await addDoc(collection(db, 'repairs'), repairWithCustomer);
    return docRef.id;
  } catch (error) {
    console.error('Error creating repair:', error);
    throw new Error('Tamirat kaydı oluşturulürken bir hata oluştu');
  }
};

/**
 * Update a repair's details
 */
const _updateRepairDetails = async (
  repairId: string,
  updates: {
    status?: RepairStatus;
    description?: string;
    priceEstimate?: number | null;
    paymentStatus?: PaymentStatus;
    paidAmount?: number;
    remainingBalance?: number;
  }
): Promise<void> => {
  if (!db) {
    throw new Error('Firestore is not initialized');
  }

  try {
    const repairRef = doc(db, 'repairs', repairId);
    
    // If payment-related fields are being updated, recalculate payment status and remaining balance
    let updatedFields = { ...updates };
    
    if (updates.paidAmount !== undefined || updates.priceEstimate !== undefined) {
      // Get current repair data to calculate new payment status
      const currentRepair = await getDoc(repairRef);
      if (currentRepair.exists()) {
        const currentData = currentRepair.data();
        const newPaidAmount = updates.paidAmount ?? currentData.paidAmount ?? 0;
        const newPriceEstimate = updates.priceEstimate ?? currentData.priceEstimate ?? 0;
        
        // Calculate new payment status
        let newPaymentStatus = updates.paymentStatus || PaymentStatus.Unpaid;
        if (newPaidAmount > 0 && newPriceEstimate > 0) {
          if (newPaidAmount >= newPriceEstimate) {
            newPaymentStatus = PaymentStatus.FullyPaid;
          } else {
            newPaymentStatus = PaymentStatus.PartiallyPaid;
          }
        } else if (newPaidAmount === 0) {
          newPaymentStatus = PaymentStatus.Unpaid;
        }
        
        const newRemainingBalance = Math.max(0, newPriceEstimate - newPaidAmount);
        
        updatedFields = {
          ...updatedFields,
          paymentStatus: newPaymentStatus,
          remainingBalance: newRemainingBalance
        };
      }
    }
    
    // Add updatedAt timestamp to the updates
    const updatesWithTimestamp = {
      ...updatedFields,
      updatedAt: serverTimestamp()
    };
    
    await updateDoc(repairRef, updatesWithTimestamp);
  } catch (error) {
    console.error('Error updating repair:', error);
    throw new Error('Tamirat güncellenirken bir hata oluştu');
  }
};

/**
 * Search for customers by name or phone number
 */
const _searchCustomers = async ({ name = '', phone = '' }: { name?: string; phone?: string }): Promise<CustomerWithId[]> => {
  if (!db) {
    throw new Error('Firestore is not initialized');
  }
  
  // Require at least one search parameter
  if (!name && !phone) {
    return [];
  }

  try {
    const userId = getUserId();
    const customersRef = collection(db, 'customers');
    
    // Process and normalize search parameters
    const nameLower = name.toLowerCase().trim();
    const phoneDigits = phone.replace(/\D/g, '');
    
    // Only search within the current user's customers
    const userCustomersQuery = query(
      customersRef,
      where('userId', '==', userId)
    );
    
    // Try to get from cache first
    let querySnapshot;
    
    try {
      querySnapshot = await getDocsFromCache(userCustomersQuery);
      
      if (querySnapshot.empty && querySnapshot.metadata.fromCache) {
        querySnapshot = await getDocsFromServer(userCustomersQuery);
      }
    } catch (cacheError) {
      console.warn('Could not fetch from cache, falling back to server:', cacheError);
      querySnapshot = await getDocsFromServer(userCustomersQuery);
    }
    
    // Filter results manually with proper matching
    const customers: CustomerWithId[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const customerName = (data.name || '').toLowerCase();
      // The phone is stored as a 10-digit number without country code
      const customerPhone = data.phone || '';
      
      // Determine matches based on provided search criteria
      const nameMatch = name ? customerName.includes(nameLower) : false;
      
      // For phone matching, extract just the digits from the stored phone number
      // to compare with the input digits
      const customerPhoneDigits = customerPhone.replace(/\D/g, '');
      const phoneMatch = phone ? customerPhoneDigits.includes(phoneDigits) : false;
      
      // Include customer if ANY of the provided criteria match
      if ((name && nameMatch) || (phone && phoneMatch)) {
        customers.push({
          id: doc.id,
          ...data
        } as CustomerWithId);
      }
    });
    
    return customers;
  } catch (error) {
    console.error('Error searching customers:', error);
    throw new Error('Müşteri araması sırasında bir hata oluştu');
  }
};

/**
 * Get active repairs for a customer
 */
const _getCustomerActiveRepairs = async (customerId: string): Promise<RepairWithId[]> => {
  if (!db) {
    throw new Error('Firestore is not initialized');
  }

  try {
    const userId = getUserId();
    const customerRef = doc(db, 'customers', customerId);
    const repairsRef = collection(db, 'repairs');
    const q = query(
      repairsRef,
      where('userId', '==', userId),
      where('customerId', '==', customerRef),
      where('pickedUp', '==', false)
    );
    
    let querySnapshot;
    
    try {
      // Try to get from cache first
      querySnapshot = await getDocsFromCache(q);
      
      if (querySnapshot.empty && querySnapshot.metadata.fromCache) {
        querySnapshot = await getDocsFromServer(q);
      }
    } catch (cacheError) {
      console.warn('Could not fetch from cache, falling back to server:', cacheError);
      querySnapshot = await getDocsFromServer(q);
    }
    
    const repairs: RepairWithId[] = [];
    querySnapshot.forEach((doc) => {
      // Explicitly type data to help TypeScript with the spread and override
      const data = doc.data() as Omit<Repair, 'createdAt' | 'updatedAt' | 'pickupTimestamp'> & {
        createdAt?: any; // Allow original timestamp to be of any type before conversion
        updatedAt?: any;
        pickupTimestamp?: any;
      };

      repairs.push({
        ...data, // Spread all fields from data, including those not in RepairWithId yet (like customerId)
        id: doc.id, // Add the id
        // Now, specifically overwrite the timestamp fields with converted Timestamp objects
        createdAt: toTimestampSafe(data.createdAt),
        updatedAt: toTimestampSafe(data.updatedAt),
        pickupTimestamp: data.pickupTimestamp ? toTimestampSafe(data.pickupTimestamp) : undefined,
      } as RepairWithId);
    });
    
    return repairs;
  } catch (error) {
    console.error('Error getting customer repairs:', error);
    throw new Error('Müşteri tamiratleri alınırken bir hata oluştu');
  }
};

/**
 * Mark a repair as picked up
 */
const _markRepairAsPickedUp = async (repairId: string, finalPrice?: number): Promise<void> => {
  if (!db) {
    throw new Error('Firestore is not initialized');
  }

  try {
    const repairRef = doc(db, 'repairs', repairId);
    
    // Get current repair data to calculate final payment status
    const currentRepair = await getDoc(repairRef);
    if (currentRepair.exists()) {
      const currentData = currentRepair.data();
      const newFinalPrice = finalPrice !== undefined ? finalPrice : currentData.priceEstimate || 0;
      
      // When marking as picked up, assume remaining balance was collected
      // Update paidAmount to equal finalPrice (full payment collected)
      const finalPaidAmount = newFinalPrice;
      const finalPaymentStatus = PaymentStatus.FullyPaid;
      const finalRemainingBalance = 0;
      
      await updateDoc(repairRef, {
        pickedUp: true,
        pickupTimestamp: serverTimestamp(),
        status: RepairStatus.PickedUp,
        updatedAt: serverTimestamp(),
        finalPrice: newFinalPrice,
        paidAmount: finalPaidAmount,
        paymentStatus: finalPaymentStatus,
        remainingBalance: finalRemainingBalance
      });
    } else {
      // Fallback if repair doesn't exist (shouldn't happen)
    await updateDoc(repairRef, {
      pickedUp: true,
      pickupTimestamp: serverTimestamp(),
      status: RepairStatus.PickedUp,
      updatedAt: serverTimestamp(),
      finalPrice: finalPrice !== undefined ? finalPrice : null
    });
    }
  } catch (error) {
    console.error('Error marking repair as picked up:', error);
    throw new Error('Tamirat teslim edildi olarak işaretlenirken bir hata oluştu');
  }
};

/**
 * Update only a repair's status
 */
const _updateRepairStatus = async (
  repairId: string,
  status: RepairStatus
): Promise<void> => {
  if (!db) {
    throw new Error('Firestore is not initialized');
  }

  try {
    const repairRef = doc(db, 'repairs', repairId);
    
    await updateDoc(repairRef, {
      status,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating repair status:', error);
    throw new Error('Tamirat durumu güncellenirken bir hata oluştu');
  }
};

/**
 * Get all repairs with pagination support and offline capabilities
 */
const _getAllRepairs = async (
  pageSize: number = 10, 
  pageNumber: number = 1, 
  statusFilter: RepairStatus | 'all' = 'all',
  lastVisibleDoc?: QueryDocumentSnapshot
): Promise<{repairs: Array<RepairWithId & { customerName: string }>, totalCount: number, lastVisible?: QueryDocumentSnapshot}> => {
  if (!db) {
    throw new Error('Firestore is not initialized');
  }

  try {
    const userId = getUserId();
    const repairsRef = collection(db, 'repairs');
    
    // Base query parts
    const orderByField = 'createdAt';
    const orderDirection = 'desc';
    
    // Build base query for fetching the page
    let repairsQuery;
    
    // First, build filter conditions - always include userId
    if (statusFilter !== 'all') {
      // Apply the status filter
      if (lastVisibleDoc) {
        // With cursor (status filter + cursor)
        repairsQuery = query(
          repairsRef,
          where('userId', '==', userId),
          where('status', '==', statusFilter),
          orderBy(orderByField, orderDirection),
          startAfter(lastVisibleDoc),
          limit(pageSize)
        );
      } 
      // If no cursor is provided but not first page, fallback to offset-based pagination
      else if (pageNumber > 1) {
        console.log('Using offset-based pagination for status filtered results');
        
        // Query to get the previous page's documents
        const previousPageQuery = query(
          repairsRef,
          where('userId', '==', userId),
          where('status', '==', statusFilter),
          orderBy(orderByField, orderDirection),
          limit((pageNumber - 1) * pageSize)
        );
        
        // For pagination, we need consistent data source (either all cache or all server)
        let previousPageSnapshot;
        
        try {
          // Try cache first for the previous page documents
          previousPageSnapshot = await getDocsFromCache(previousPageQuery);
          
          // If we get empty results from cache but know it's from cache, get from server
          if (previousPageSnapshot.empty && previousPageSnapshot.metadata.fromCache) {
            console.log('Previous page cache empty or miss, falling back to server');
            previousPageSnapshot = await getDocsFromServer(previousPageQuery);
          }
        } catch (error) {
          console.warn('Could not fetch previous page from cache, using server:', error);
          previousPageSnapshot = await getDocsFromServer(previousPageQuery);
        }
        
        // Ensure we have documents to use for pagination
        if (previousPageSnapshot.empty) {
          console.warn('No documents found for the previous page, returning empty result');
          return { repairs: [], totalCount: 0 };
        }
        
        const lastVisible = previousPageSnapshot.docs[previousPageSnapshot.docs.length - 1];
        
        // Apply startAfter to get next page
        repairsQuery = query(
          repairsRef,
          where('userId', '==', userId),
          where('status', '==', statusFilter),
          orderBy(orderByField, orderDirection),
          startAfter(lastVisible),
          limit(pageSize)
        );
      } 
      // First page with status filter
      else {
        repairsQuery = query(
          repairsRef,
          where('userId', '==', userId),
          where('status', '==', statusFilter),
          orderBy(orderByField, orderDirection),
          limit(pageSize)
        );
      }
    } 
    // No status filter
    else {
      if (lastVisibleDoc) {
        // With cursor (no status filter, just cursor)
        repairsQuery = query(
          repairsRef,
          where('userId', '==', userId),
          orderBy(orderByField, orderDirection),
          startAfter(lastVisibleDoc),
          limit(pageSize)
        );
      } 
      // If no cursor is provided but not first page, fallback to offset-based pagination
      else if (pageNumber > 1) {
        console.log('Using offset-based pagination for all results');
        
        // Query to get the previous page's documents
        const previousPageQuery = query(
          repairsRef,
          where('userId', '==', userId),
          orderBy(orderByField, orderDirection),
          limit((pageNumber - 1) * pageSize)
        );
        
        // For pagination, we need consistent data source (either all cache or all server)
        let previousPageSnapshot;
        
        try {
          // Try cache first for the previous page documents
          previousPageSnapshot = await getDocsFromCache(previousPageQuery);
          
          // If we get empty results from cache but know it's from cache, get from server
          if (previousPageSnapshot.empty && previousPageSnapshot.metadata.fromCache) {
            console.log('Previous page cache empty or miss, falling back to server');
            previousPageSnapshot = await getDocsFromServer(previousPageQuery);
          }
        } catch (error) {
          console.warn('Could not fetch previous page from cache, using server:', error);
          previousPageSnapshot = await getDocsFromServer(previousPageQuery);
        }
        
        // Ensure we have documents to use for pagination
        if (previousPageSnapshot.empty) {
          console.warn('No documents found for the previous page, returning empty result');
          return { repairs: [], totalCount: 0 };
        }
        
        const lastVisible = previousPageSnapshot.docs[previousPageSnapshot.docs.length - 1];
        
        // Apply startAfter to get next page
        repairsQuery = query(
          repairsRef,
          where('userId', '==', userId),
          orderBy(orderByField, orderDirection),
          startAfter(lastVisible),
          limit(pageSize)
        );
      } 
      // First page without status filter
      else {
        repairsQuery = query(
          repairsRef,
          where('userId', '==', userId),
          orderBy(orderByField, orderDirection),
          limit(pageSize)
        );
      }
    }
    
    // Now fetch the actual data
    let snapshot;
    const sourceName = lastVisibleDoc ? 'cursor-based' : (pageNumber > 1 ? 'offset-based' : 'first page');
    
    try {
      // Try to get from cache first
      snapshot = await getDocsFromCache(repairsQuery);
      
      // If we get empty results from cache but know it's from cache, get from server
      if (snapshot.empty && snapshot.metadata.fromCache) {
        snapshot = await getDocsFromServer(repairsQuery);
      }
    } catch (cacheError) {
      console.warn('Could not fetch from cache, falling back to server:', cacheError);
      snapshot = await getDocsFromServer(repairsQuery);
    }
    
    // Store the last visible document for future pagination
    const lastVisible = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : undefined;
    
    // Step 1: Extract repair data and collect unique customer references
    const repairsData: Array<RepairWithId & { customerName: string }> = [];
    const customerRefs: DocumentReference[] = [];
    const repairToCustomerRefMap = new Map<string, string>();
    
    snapshot.docs.forEach(document => {
      const repairData = document.data();
      const customerId = repairData.customerId;

      // Store the customer reference by its ID or path if it exists
      if (customerId) {
        const customerKey = customerId?.id || customerId?.path || '';
        // Add to list of refs to load if not already there (batchLoadCustomers will handle actual fetching)
        // We collect all unique customer references first
        if (customerKey && !customerRefs.some(ref => (ref.id || ref.path) === customerKey)) {
            customerRefs.push(customerId);
        }
        repairToCustomerRefMap.set(document.id, customerKey);
      }
      
      // Add repair with placeholder name, to be filled after batch loading customers
      repairsData.push({
        id: document.id,
        ...repairData,
        customerName: 'Bilinmeyen Müşteri' // Default placeholder
      } as RepairWithId & { customerName: string });
    });
    
    // Step 2: Batch load all unique customer data
    if (customerRefs.length > 0) {
      const customerDataMap = await batchLoadCustomers(customerRefs);
      
      // Step 3: Update repair data with customer names
      repairsData.forEach(repair => {
        const customerKey = repairToCustomerRefMap.get(repair.id);
        if (customerKey && customerDataMap.has(customerKey)) {
          repair.customerName = customerDataMap.get(customerKey)?.name || 'Bilinmeyen Müşteri';
        }
      });
    }
    
    // Check if there's likely a next page by comparing the number of items returned to the page size
    const hasNextPage = repairsData.length === pageSize;
    
    // Calculate estimated total based on current page information
    // This is an estimation and only accurate for the first page or when fewer items than pageSize
    const totalEstimate = pageNumber === 1 && repairsData.length < pageSize
      ? repairsData.length // If first page has fewer items than requested, that's the total
      : pageNumber * pageSize + (hasNextPage ? pageSize : 0); // Otherwise estimate based on current page
    
    return { 
      repairs: repairsData, 
      totalCount: totalEstimate,
      lastVisible // Return the last document to use as cursor for next page
    };
  } catch (error) {
    console.error('Error fetching repairs:', error);
    throw new Error('Tamirat listesi alınırken bir hata oluştu');
  }
}; 

/**
 * Set up real-time listener for repairs changes
 * @param callback Function to call when repairs data changes
 * @param statusFilter Optional status filter
 * @param limit Maximum number of repairs to listen to
 * @returns Unsubscribe function to stop listening
 */
const _subscribeToRepairs = async (
  callback: (repairs: Array<RepairWithId & { customerName: string }>) => void,
  statusFilter: RepairStatus | 'all' = 'all',
  limitCount: number = 20
): Promise<Unsubscribe> => {
  if (!db) {
    throw new Error('Firestore is not initialized');
  }

  try {
    const userId = getUserId();
    const repairsRef = collection(db, 'repairs');
    
    // Build query based on filters - always include userId
    let repairsQuery = query(
      repairsRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    
    if (statusFilter !== 'all') {
      repairsQuery = query(
        repairsRef,
        where('userId', '==', userId),
        where('status', '==', statusFilter),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
    }
    
    // Set up real-time listener
    return onSnapshot(
      repairsQuery,
      async (snapshot) => {
        // Process repairs similar to getAllRepairs
        const repairsData: Array<RepairWithId & { customerName: string }> = [];
        const customerRefsMap = new Map<string, DocumentReference>();
        
        snapshot.docs.forEach(document => {
          const repairData = document.data();
          const customerId = repairData.customerId;
          
          const customerKey = customerId?.id || customerId?.path || '';
          if (customerKey && !customerRefsMap.has(customerKey)) {
            customerRefsMap.set(customerKey, customerId);
          }
          
          repairsData.push({
            id: document.id,
            ...repairData,
            customerName: 'Bilinmeyen Müşteri'
          } as RepairWithId & { customerName: string });
        });
        
        if (customerRefsMap.size > 0) {
          const customerRefs = Array.from(customerRefsMap.values());
          const customerSnapshots = await Promise.all(
            customerRefs.map(ref => getDoc(ref))
          );
          
          const customerDataMap = new Map<string, Customer>();
          
          customerSnapshots.forEach(snapshot => {
            if (snapshot.exists()) {
              const refKey = snapshot.ref.id || snapshot.ref.path || '';
              customerDataMap.set(refKey, snapshot.data() as Customer);
            }
          });
          
          repairsData.forEach(repair => {
            const customerRef = repair.customerId;
            const customerKey = customerRef?.id || customerRef?.path || '';
            
            if (customerKey && customerDataMap.has(customerKey)) {
              repair.customerName = customerDataMap.get(customerKey)?.name || 'Bilinmeyen Müşteri';
            }
          });
        }
        
        // Call the callback with the processed data
        callback(repairsData);
      },
      (error) => {
        console.error('Error in repairs subscription:', error);
      }
    );
  } catch (error) {
    console.error('Error setting up repairs subscription:', error);
    throw new Error('Tamirat aboneliği kurulurken bir hata oluştu');
  }
};

/**
 * Set up real-time listener for a single customer
 * @param customerId ID of the customer to listen to
 * @param callback Function to call when the customer data changes
 * @returns Unsubscribe function to stop listening
 */
const _subscribeToCustomer = async (
  customerId: string,
  callback: (customer: CustomerWithId | null) => void
): Promise<Unsubscribe> => {
  if (!db) {
    throw new Error('Firestore is not initialized');
  }

  try {
    const customerRef = doc(db, 'customers', customerId);
    
    // Set up real-time listener for the specific document
    return onSnapshot(
      customerRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          const userId = getUserId();
          
          // Ensure the customer belongs to the current user
          if (data.userId !== userId) {
            console.warn('Attempted to access customer belonging to different user');
            callback(null);
            return;
          }
          
          const customer = {
            id: docSnap.id,
            ...data
          } as CustomerWithId;
          
          callback(customer);
        } else {
          // Document doesn't exist
          callback(null);
        }
      },
      (error) => {
        console.error(`Error in customer subscription for id ${customerId}:`, error);
        callback(null);
      }
    );
  } catch (error) {
    console.error(`Error setting up customer subscription for id ${customerId}:`, error);
    throw new Error('Müşteri aboneliği kurulurken bir hata oluştu');
  }
};

/**
 * Set up real-time listener for customers matching a search query
 * @param searchParams Search parameters for customers (name, phone)
 * @param callback Function to call when customer data changes
 * @param limitCount Maximum number of customers to listen to
 * @returns Unsubscribe function to stop listening
 */
const _subscribeToCustomers = async (
  searchParams: { name?: string; phone?: string } = {},
  callback: (customers: CustomerWithId[]) => void,
  limitCount: number = 20
): Promise<Unsubscribe> => {
  if (!db) {
    throw new Error('Firestore is not initialized');
  }

  try {
    const userId = getUserId();
    const customersRef = collection(db, 'customers');
    
    // Start with base query including userId
    let customersQuery = query(
      customersRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    
    // For phone searches, we can add a filter if phone number is provided
    // But for name searches, we'll need to filter in memory since Firestore 
    // doesn't support substring searches
    if (searchParams.phone) {
      const normalizedPhone = normalizePhoneNumber(searchParams.phone);
      customersQuery = query(
        customersRef,
        where('userId', '==', userId),
        where('phone', '==', normalizedPhone),
        limit(limitCount)
      );
    }
    
    // Set up real-time listener
    return onSnapshot(
      customersQuery,
      (snapshot) => {
        let customers = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as CustomerWithId));
        
        // If name search is provided, filter in memory
        if (searchParams.name) {
          const nameLower = searchParams.name.toLowerCase().trim();
          customers = customers.filter(customer => 
            (customer.name || '').toLowerCase().includes(nameLower)
          );
        }
        
        // Call the callback with the processed data
        callback(customers);
      },
      (error) => {
        console.error('Error in customers subscription:', error);
      }
    );
  } catch (error) {
    console.error('Error setting up customers subscription:', error);
    throw new Error('Müşteriler aboneliği kurulurken bir hata oluştu');
  }
};

/**
 * Set up real-time listener for customer repairs
 * @param customerId ID of the customer to get repairs for
 * @param callback Function to call when customer repairs data changes
 * @param activeOnly Whether to only retrieve active (not picked up) repairs
 * @returns Unsubscribe function to stop listening
 */
const _subscribeToCustomerRepairs = async (
  customerId: string,
  callback: (repairs: RepairWithId[]) => void,
  activeOnly: boolean = true
): Promise<Unsubscribe> => {
  if (!db) {
    throw new Error('Firestore is not initialized');
  }

  try {
    const userId = getUserId();
    const customerRef = doc(db, 'customers', customerId);
    const repairsRef = collection(db, 'repairs');
    
    // Build query with appropriate filters
    let repairsQuery;
    
    if (activeOnly) {
      repairsQuery = query(
        repairsRef,
        where('userId', '==', userId),
        where('customerId', '==', customerRef),
        where('pickedUp', '==', false),
        orderBy('createdAt', 'desc')
      );
    } else {
      repairsQuery = query(
        repairsRef,
        where('userId', '==', userId),
        where('customerId', '==', customerRef),
        orderBy('createdAt', 'desc')
      );
    }
    
    // Set up real-time listener
    return onSnapshot(
      repairsQuery,
      (snapshot) => {
        const repairs = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as RepairWithId));
        
        // Call the callback with the processed data
        callback(repairs);
      },
      (error) => {
        console.error(`Error in customer repairs subscription for customer ${customerId}:`, error);
      }
    );
  } catch (error) {
    console.error(`Error setting up customer repairs subscription for customer ${customerId}:`, error);
    throw new Error('Müşteri tamiratleri aboneliği kurulurken bir hata oluştu');
  }
};

/**
 * Get a customer by ID with caching support
 * @param customerId ID or reference of the customer to retrieve
 * @returns CustomerWithId object or null if not found
 */
const _getCustomerById = async (customerId: string | DocumentReference): Promise<CustomerWithId | null> => {
  if (!db) {
    throw new Error('Firestore is not initialized');
  }
  
  // Convert reference to string key for cache lookup
  const customerKey = typeof customerId === 'string' ? customerId : (customerId?.id || customerId?.path || '');
  if (!customerKey) return null;
  
  try {
    // Get from Firestore; SDK handles its own caching
    const customerRef = typeof customerId === 'string' ? doc(db, 'customers', customerId) : customerId;
    const customerDoc = await getDoc(customerRef);
    
    if (customerDoc.exists()) {
      const customerData = customerDoc.data() as Customer;
      return {
        ...customerData,
        id: customerDoc.id
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching customer:', error);
    throw new Error('Müşteri bilgileri alınamadı');
  }
};

const _getPickedUpRepairsByDateRange = async (
  startDate: Date,
  endDate: Date
): Promise<RepairWithId[]> => {
  if (!db) {
    throw new Error('Firestore is not initialized');
  }

  try {
    const userId = getUserId();
    const repairsRef = collection(db, 'repairs');
    const q = query(
      repairsRef,
      where('userId', '==', userId),
      where('status', '==', RepairStatus.PickedUp),
      where('pickupTimestamp', '>=', Timestamp.fromDate(startDate)),
      where('pickupTimestamp', '<=', Timestamp.fromDate(endDate)),
      orderBy('pickupTimestamp', 'desc')
    );

    let querySnapshot;
    try {
      querySnapshot = await getDocsFromCache(q);
      if (querySnapshot.empty && querySnapshot.metadata.fromCache) {
        querySnapshot = await getDocsFromServer(q);
      }
    } catch (cacheError) {
      querySnapshot = await getDocsFromServer(q);
    }

    const repairs: RepairWithId[] = [];
    querySnapshot.forEach((doc) => {
      // Explicitly type data to help TypeScript with the spread and override
      const data = doc.data() as Omit<Repair, 'createdAt' | 'updatedAt' | 'pickupTimestamp'> & {
        createdAt?: any; // Allow original timestamp to be of any type before conversion
        updatedAt?: any;
        pickupTimestamp?: any;
      };

      repairs.push({
        ...data, // Spread all fields from data, including those not in RepairWithId yet (like customerId)
        id: doc.id, // Add the id
        // Now, specifically overwrite the timestamp fields with converted Timestamp objects
        createdAt: toTimestampSafe(data.createdAt),
        updatedAt: toTimestampSafe(data.updatedAt),
        pickupTimestamp: data.pickupTimestamp ? toTimestampSafe(data.pickupTimestamp) : undefined,
      } as RepairWithId);
    });
    return repairs;
  } catch (error) {
    console.error('Error getting picked-up repairs by date range:', error);
    throw new Error('Teslim edilen tamiratlar alınırken bir hata oluştu.');
  }
};

/**
 * Preload repair data for faster access
 * This function prefetches common repair data to improve initial load performance
 */
const _preloadRepairData = async (): Promise<void> => {
  if (!db) {
    throw new Error('Firestore is not initialized');
  }

  try {
    const userId = getUserId();
    const repairsRef = collection(db, 'repairs');
    
    // Preload active repairs (not picked up)
    const activeRepairsQuery = query(
      repairsRef,
      where('userId', '==', userId),
      where('pickedUp', '==', false),
      orderBy('createdAt', 'desc'),
      limit(20)
    );
    
    // Simply trigger the fetch to populate cache
    await getDocsFromServer(activeRepairsQuery);
    
    return;
  } catch (error) {
    console.error('Error preloading repair data:', error);
    // Don't throw error here to prevent app initialization issues
    // Just log the error and continue
  }
};



// Export the functions with authentication wrapper
export const toggleNetwork = withAuthentication(_toggleNetwork);
export const findCustomerByPhone = withAuthentication(_findCustomerByPhone);
export const createCustomer = withAuthentication(_createCustomer);
export const createRepair = withAuthentication(_createRepair);
export const updateRepairDetails = withAuthentication(_updateRepairDetails);
export const searchCustomers = withAuthentication(_searchCustomers);
export const getCustomerActiveRepairs = withAuthentication(_getCustomerActiveRepairs);
export const markRepairAsPickedUp = withAuthentication(_markRepairAsPickedUp);
export const updateRepairStatus = withAuthentication(_updateRepairStatus);
export const getAllRepairs = withAuthentication(_getAllRepairs);
export const subscribeToRepairs = withAuthentication(_subscribeToRepairs);
export const subscribeToCustomer = withAuthentication(_subscribeToCustomer);
export const subscribeToCustomers = withAuthentication(_subscribeToCustomers);
export const subscribeToCustomerRepairs = withAuthentication(_subscribeToCustomerRepairs);
export const getCustomerById = withAuthentication(_getCustomerById);
export const getPickedUpRepairsByDateRange = withAuthentication(_getPickedUpRepairsByDateRange);
export const preloadRepairData = withAuthentication(_preloadRepairData); 